import { CoordinateSystemInfoProvider, T_ICoordinateSystemInfoProvider } from '@cgs/common';
import { FreeSpinsInfoEvent, SpinResponse } from '@cgs/data-access';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  IGameStateMachineProvider,
  ResourcesComponent,
  RESOURCES_COMPONENT_TOKEN,
} from '@cgs/machines';
import {
  Compatibility,
  Container,
  debounce,
  Rect,
  SceneObject,
  ScreenAlignmentObject,
  Vector2,
  viewportChangeListener,
} from '@cgs/wge';

export class CorsicaOrientationHandlerComponent {
  private readonly horizontalNode: SceneObject;
  private readonly verticalNode: SceneObject;
  private readonly coordinateSystem: Rect;
  private readonly canvas: HTMLCanvasElement;
  protected readonly root: SceneObject;

  private readonly horizontalProportion: number;
  private readonly verticalProportion: number;

  private readonly trifectaHudOriginalPosition: Vector2 | null;
  private readonly trifectaHudScreenNodeBottomHUD: ScreenAlignmentObject | null;

  protected readonly gameStateMachine: GameStateMachine<SpinResponse>;

  private modeOriginalPosition: Vector2 | null = null;

  constructor(container: Container) {
    const resourceComponent = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    this.root = resourceComponent.root;

    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    this.horizontalNode = this.root.findById<SceneObject>('sizeNodeHorizontal')!;
    this.verticalNode = this.root.findById<SceneObject>('sizeNodeVertical')!;

    const coordinateSystemInfoProvider = container.resolve<CoordinateSystemInfoProvider>(
      T_ICoordinateSystemInfoProvider
    );

    this.coordinateSystem = coordinateSystemInfoProvider.coordinateSystem;
    this.canvas = coordinateSystemInfoProvider.canvas;

    this.horizontalProportion = this.horizontalNode.size.x / this.horizontalNode.size.y;
    this.verticalProportion = this.verticalNode.size.y / this.verticalNode.size.x;

    const trifectaRootScene = this.root.findById('trifecta_hud');
    this.trifectaHudScreenNodeBottomHUD =
      trifectaRootScene?.findById<ScreenAlignmentObject>('screenNodeBottomHUD') || null;

    this.trifectaHudOriginalPosition =
      this.trifectaHudScreenNodeBottomHUD?.position.clone() || null;

    const debouncedResize = debounce(() => this.resize(), 100);

    viewportChangeListener.on(debouncedResize);

    this.gameStateMachine.init.entered.listen(() => this.resize());
    this.gameStateMachine.beginFreeSpinsPopup.leaved.listen(() => this.resize());
    this.gameStateMachine.endFreeSpinsPopup.leaved.listen(() => this.resize());

    this.resize();
  }

  protected resize(): void {
    const modesParent = this.root.findById('Modes')!.parent!;

    const modes = this.root.findById('Modes')!;

    if (!modes) {
      return;
    }

    if (!this.modeOriginalPosition) {
      this.modeOriginalPosition = modes.position.clone();
    }

    modes.position = this.modeOriginalPosition.clone();

    if (!this.horizontalNode || !this.verticalNode) {
      return;
    }

    const isFreeSpins =
      this.gameStateMachine.curResponse &&
      this.gameStateMachine.curResponse.isFreeSpins &&
      this.gameStateMachine.curResponse.freeSpinsInfo!.event !==
        FreeSpinsInfoEvent.FreeSpinsFinished;

    if (!Compatibility.IsMobileBrowser) {
      const verticalProportion = this.verticalNode.size.y / this.verticalNode.size.x;
      modesParent.scale = new Vector2(verticalProportion, verticalProportion);

      this.root.findAllById('hud_mode')?.forEach((hudMode) => {
        hudMode.stateMachine!.switchToState('landscape');
      });
      modes.stateMachine!.switchToState('horizontal');

      if (this.trifectaHudScreenNodeBottomHUD) {
        this.trifectaHudScreenNodeBottomHUD.position = new Vector2(
          this.trifectaHudOriginalPosition!.x - 100,
          this.trifectaHudOriginalPosition!.y
        );
      }
      return;
    }

    if (!Compatibility.isPortrait) {
      const windowRatio = window.innerHeight / window.innerWidth;
      const canvasRatio = this.canvas.height / this.canvas.width;

      const ratio = Math.max(windowRatio, canvasRatio);

      const commonProportion = this.coordinateSystem.size.x / this.horizontalNode.size.x;

      const proportionCoefficient = isFreeSpins ? 0.8 : 1;

      const proportion =
        Math.max(this.verticalProportion, this.horizontalProportion) * proportionCoefficient;

      const scale = Math.min(ratio * proportion, commonProportion);
      modesParent.scale = new Vector2(scale, scale);

      this.root.findAllById('hud_mode')?.forEach((hudMode) => {
        hudMode.stateMachine!.switchToState('landscape');
      });
      modes.stateMachine!.switchToState('horizontal');
    }

    if (Compatibility.isPortrait) {
      const windowRatio = window.innerWidth / window.innerHeight;
      const canvasRatio = this.canvas.width / this.canvas.height;

      const ratio = Math.max(windowRatio, canvasRatio);

      const commonProportion = this.coordinateSystem.size.y / this.verticalNode.size.y;

      const proportionCoefficient = isFreeSpins ? 1 : 1.2;

      const proportion =
        Math.max(this.verticalProportion, this.horizontalProportion) * proportionCoefficient;

      const scale = Math.max(ratio * proportion, commonProportion);
      modesParent.scale = new Vector2(scale, scale);

      modes.position = this.modeOriginalPosition.clone().subtract(new Vector2(0, 75));

      this.root.findAllById('hud_mode')?.forEach((hudMode) => {
        hudMode.stateMachine!.switchToState('portrait');
      });
      modes.stateMachine!.switchToState('vertical');
    }
  }

  public update(): void {
    this.resize();
  }
}
